import geojsonValidation from 'geojson-validation'
import dedent from 'ts-dedent'
import {
  Provider,
  QueryAndOutputs,
  WorkflowCodeType,
  WorkflowComponentCheckInputValuesParams,
  WorkflowComponentCodeParams,
  WorkflowComponentParamType,
  WorkflowComponentValue,
  WorkflowParameterValueErrorType
} from '../types'
import { isExpressionValue } from '../utils'
import { SingleOutputWorkflowComponent } from './common/singleoutputcomponent'

class TableFromGeojson extends SingleOutputWorkflowComponent {
  constructor() {
    super(
      {
        name: 'native.tablefromgeojson',
        group: 'Parsers',
        title: 'Table from GeoJSON',
        description: `
        This component creates a new table from a GeoJSON string.
      `,
        starred: true
      },
      [
        {
          name: 'geojson',
          type: WorkflowComponentParamType.GeoJson,
          title: 'GeoJSON string',
          placeholder: dedent`E.g.:
                          {
                          "type": "FeatureCollection",
                          "features": [
                            {
                              "type": "Feature",
                              "properties": {},
                              "geometry": {
                                "coordinates":
                                    [
                                      -9.90157740958361,
                                      43.97667987529289
                                    ],
                                "type": "Point"
                              }
                            }
                          ]
                          }`,
          allowExpressions: false
        }
      ]
    )
  }

  checkInputValues({
    params,
    resolvedParamValues,
    connection,
    dryRun,
    schemas,
    variables
  }: WorkflowComponentCheckInputValuesParams) {
    const problems = super.checkInputValues({
      params,
      resolvedParamValues,
      connection,
      dryRun,
      schemas,
      variables
    })
    if (resolvedParamValues.geojson && !isExpressionValue(params.geojson as WorkflowComponentValue)) {
      try {
        const geojson = JSON.parse(resolvedParamValues.geojson as string)
        if (!geojsonValidation.valid(geojson)) {
          problems.push({
            parameter: 'geojson',
            type: WorkflowParameterValueErrorType.WrongValue,
            message: 'Invalid GeoJSON string'
          })
        }
      } catch (e) {
        problems.push({
          parameter: 'geojson',
          type: WorkflowParameterValueErrorType.WrongValue,
          message: 'Invalid GeoJSON string'
        })
      }
    }
    return problems
  }

  async getCode({
    workflow,
    params,
    connection,
    componentName,
    codeType
  }: WorkflowComponentCodeParams): Promise<QueryAndOutputs> {
    const dryRun = codeType === WorkflowCodeType.DryRun
    const outputTablename = connection.getOutputTableName(workflow.id, componentName, params, 'result', codeType)
    const geojson = JSON.parse(params.geojson as string)
    const sqlFeatures: Array<string> = []
    let features: Array<Record<string, unknown>> = []
    if (geojson?.type === 'Feature') {
      features = [geojson]
    } else if (geojson?.type === 'FeatureCollection') {
      features = geojson.features
    } else {
      throw new Error('Invalid GeoJSON')
    }

    // For Oracle, analyze all string properties to find maximum lengths
    // and sort features so the one with the longest strings comes first
    const maxStringLengths: Record<string, number> = {}
    if (connection.type === Provider.Oracle) {
      for (const feature of features) {
        const properties = feature.properties as Record<string, unknown>
        for (const prop in properties) {
          const value = properties[prop]
          if (typeof value === 'string') {
            const length = value.length
            maxStringLengths[prop] = Math.max(maxStringLengths[prop] || 0, length)
          }
        }
      }

      // Sort features so the one with the maximum total string length comes first
      // This ensures Oracle creates columns with the right size from the first row
      features.sort((a, b) => {
        const aTotalLength = Object.values(a.properties as Record<string, unknown>)
          .filter((v) => typeof v === 'string')
          .reduce((sum, v) => sum + (v as string).length, 0)
        const bTotalLength = Object.values(b.properties as Record<string, unknown>)
          .filter((v) => typeof v === 'string')
          .reduce((sum, v) => sum + (v as string).length, 0)
        return bTotalLength - aTotalLength
      })
    }

    for (const feature of features) {
      const columns = {}
      const properties = feature.properties as Record<string, unknown>
      for (const prop in properties) {
        const value = properties[prop]
        if (typeof value === 'string') {
          if (connection.type === Provider.BigQuery) {
            columns[prop] = `'${value.replace(/'/g, "\\'")}'`
          } else if (connection.type === Provider.Snowflake) {
            columns[prop] = `TO_CHAR('${value.replace(/'/g, "''")}')`
          } else if (connection.type === Provider.Redshift) {
            columns[prop] = `'${value.replace(/'/g, "''")}'::VARCHAR(MAX)`
          } else if (connection.type === Provider.Oracle) {
            columns[prop] = `'${value.replace(/'/g, "''")}'`
          } else {
            columns[prop] = `'${value.replace(/'/g, "''")}'`
          }
        } else {
          columns[prop] = value
        }
      }
      if (feature.geometry) {
        if (connection.type === Provider.BigQuery) {
          columns['geom'] = dedent`ST_GEOGFROM(
                                  '${JSON.stringify(feature.geometry)}'
                                )`
        } else if (connection.type === Provider.Snowflake) {
          columns['geom'] = dedent`TO_GEOGRAPHY(
                                  '${JSON.stringify(feature.geometry)}'
                                )`
        } else if (connection.type === Provider.Oracle) {
          columns['geom'] = dedent`SDO_UTIL.FROM_GEOJSON(
                                  '${JSON.stringify(feature.geometry)}'
                                )`
        } else {
          columns['geom'] = dedent`${connection.processOutputGeogColumn(`ST_GEOMFROMGEOJSON(
  '${JSON.stringify(feature.geometry)}'
)`)}`
        }
      }
      sqlFeatures.push(
        Object.keys(columns)
          .map((x) => `${columns[x]} AS ${x}`)
          .join(',\n')
      )
    }
    if (codeType === WorkflowCodeType.Full) {
      codeType = WorkflowCodeType.FullNoCache
    } // to avoid adding features to the same table again when re-running the workflow
    let query = ''

    if (connection.type === Provider.Oracle) {
      // For Oracle, create table structure explicitly first to avoid column size inference issues
      const columns: string[] = []
      const firstFeature = features[0]
      const properties = firstFeature.properties as Record<string, unknown>

      for (const prop in properties) {
        const value = properties[prop]
        if (typeof value === 'string') {
          const maxLength = Math.min(Math.max(maxStringLengths[prop] || 100, 100), 4000)
          columns.push(`${prop} VARCHAR2(${maxLength})`)
        } else if (typeof value === 'number') {
          columns.push(`${prop} NUMBER`)
        }
      }

      if (firstFeature.geometry) {
        columns.push('geom SDO_GEOMETRY')
      }

      query = dedent`
        BEGIN
          EXECUTE IMMEDIATE 'DROP TABLE ${connection.quoteName(outputTablename)}';
        EXCEPTION
          WHEN OTHERS THEN
              IF SQLCODE = -942 THEN
                NULL; -- Table doesn't exist, continue
              ELSE
                RAISE; -- Raise other unexpected errors
              END IF;
        END;
        
        EXECUTE IMMEDIATE 'CREATE TABLE ${connection.quoteName(outputTablename)} (${columns.join(', ')})';
        
        EXECUTE IMMEDIATE q'[
          INSERT INTO ${connection.quoteName(outputTablename)}
          SELECT
            ${sqlFeatures[0]}
          FROM DUAL
        ]';
        
        ${
          !dryRun
            ? sqlFeatures
                .slice(1)
                .map(
                  (x) => dedent`
                    EXECUTE IMMEDIATE q'[
                      INSERT INTO ${connection.quoteName(outputTablename)}
                      SELECT
                        ${x}
                      FROM DUAL
                    ]';
                  `
                )
                .join('\n')
            : ''
        }
        
        COMMIT;`
    } else {
      query =
        connection.type === Provider.DatabricksRest
          ? connection.getCreateTempTableQuery(
              outputTablename,
              dedent`SELECT /*+ REPARTITION(40, geom) */
                  ${sqlFeatures[0]}
                `,
              codeType,
              null,
              false
            )
          : connection.getCreateTempTableQuery(
              outputTablename,
              dedent`SELECT
                  ${sqlFeatures[0]}
                `,
              codeType,
              null
            )
    }

    // For Oracle, don't use wrapCodeInIfTableExistsBlock as it causes transaction isolation issues
    // The table creation already has proper error handling built in

    if (!dryRun && connection.type !== Provider.Oracle) {
      query += '\n'
      if (connection.type === Provider.DatabricksRest) {
        query += dedent(
          sqlFeatures
            .slice(1)
            .map(
              (x) => dedent`
                        INSERT INTO ${connection.quoteName(outputTablename)}
                        SELECT /*+ REPARTITION(40, geom) */
                          ${x};
                      `
            )
            .join('\n')
        )
      } else {
        query += dedent(
          sqlFeatures
            .slice(1)
            .map(
              (x) => dedent`
                      INSERT INTO ${connection.quoteName(outputTablename)}
                      SELECT
                        ${x};
                    `
            )
            .join('\n')
        )
      }
    }
    if (connection.type !== Provider.DatabricksRest) {
      query = connection.wrapCodeInIfTableExistsBlock(query, [outputTablename], codeType)
    } else {
      query = dedent`
        ${query}
        ${connection.optimizationStatsQuery(outputTablename)}`
    }
    return {
      query,
      outputs: { result: outputTablename }
    }
  }

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  getVersion(provider: Provider): string {
    return '2'
  }
  supportedProviders(): Provider[] {
    return [
      Provider.BigQuery,
      Provider.Postgres,
      Provider.Redshift,
      Provider.Snowflake,
      Provider.DatabricksRest,
      Provider.Oracle
    ]
  }
}

export { TableFromGeojson }
